import { useState, useEffect } from 'react'
import { getSurveyResults } from '@/services/survey'
import { listClusters, type ClusterResponse } from '@/services/cluster'
import ModalityRadar from '@/component/modality/modality-radar'
import { ReadMoreModal } from './survey-read-more-modal'
import { ArrowRight } from 'lucide-react'
import { PoweredByAida } from '@/component/ui/powered-by-aida'
import { PageContainer } from '@/component/layout'

interface ClusterScore {
  clusterId: number
  totalScore: number
  percentage: number
  name: string
  description: string
  longFormDescriptor?: string
  image?: string
}

interface SurveyResultsDashboardProps {
  userName: string
  surveyId?: number
}

export const SurveyResultsDashboard = ({ userName, surveyId }: SurveyResultsDashboardProps) => {
  const [clusterScores, setClusterScores] = useState<ClusterScore[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedCluster, setSelectedCluster] = useState<ClusterScore | null>(null)

  // Optional fallback (UI only) if API copy is missing
  const fallbackDefs: Record<number, { name: string; description: string; image?: string }> = {
    1: {
      name: 'Experiential Learning',
      description: 'Description not available',
      image: '/images/experiential.jpg',
    },
    2: {
      name: 'Cooperative Learning',
      description: 'Description not available',
      image: '/images/experiential.jpg',
    },
    3: {
      name: 'Goal-Driven Learning',
      description: 'Description not available',
      image: '/images/experiential.jpg',
    },
    4: {
      name: 'Mastery-Based Learning',
      description: 'Description not available',
      image: '/images/experiential.jpg',
    },
  }

  useEffect(() => {
    if (surveyId) void loadResults()
  }, [surveyId])

  // This formaula calculates the percentage score for each cluster based on the total scores of all clusters. It ensures that the percentages sum up to 100% and handles cases where the total score is zero to avoid division by zero errors.
  // const loadResults = async () => {
  //   try {
  //     setLoading(true)
  //     const data = await getSurveyResults(surveyId!)
  //     const totalSum = data.clusterScores.reduce((sum: number, s: any) => sum + s.totalScore, 0)

  //     const processed: ClusterScore[] = data.clusterScores
  //       .map((s: any) => ({
  //         ...s,
  //         percentage: Math.round((s.totalScore / Math.max(totalSum, 1)) * 100),
  //         name: clusterDefinitions[s.clusterId as 1 | 2 | 3 | 4]?.name ?? `Cluster ${s.clusterId}`,
  //         description:
  //           clusterDefinitions[s.clusterId as 1 | 2 | 3 | 4]?.description ??
  //           'Description not available',
  //       }))
  //       .sort((a: ClusterScore, b: ClusterScore) => b.percentage - a.percentage)

  //     // remove setResults(data)
  //     setClusterScores(processed)
  //   } catch (err: any) {
  //     setError(err.message || 'Failed to load results')
  //   } finally {
  //     setLoading(false)
  //   }
  // }

  // Revised formula to calculate percentage based on a fixed maximum score per cluster
  const loadResults = async () => {
    try {
      setLoading(true)

      // Fetch survey results + cluster metadata in parallel
      const [data, clusterList] = await Promise.all([getSurveyResults(surveyId!), listClusters(1000, 0)])

      // Map metadata by id for fast lookups
      const metaById = new Map<number, Pick<ClusterResponse, 'name' | 'description' | 'longFormDescriptor'>>()
      for (const c of clusterList.data) {
        metaById.set(Number(c.id), {
          name: c.name,
          description: c.description,
          longFormDescriptor: c.longFormDescriptor,
        })
      }

      const MAX_PER_CLUSTER = 25

      const processed: ClusterScore[] = data.clusterScores
        .map((s: any) => {
          const pct = Math.round((s.totalScore / MAX_PER_CLUSTER) * 100)
          const meta = metaById.get(Number(s.clusterId))
          const fb = fallbackDefs[s.clusterId as 1 | 2 | 3 | 4]

          return {
            ...s,
            percentage: Math.max(0, Math.min(100, pct)),
            name: meta?.name ?? fb?.name ?? `Cluster ${s.clusterId}`,
            description: meta?.description ?? fb?.description ?? 'Description not available',
            longFormDescriptor: meta?.longFormDescriptor,
            image: fb?.image,
          }
        })
        .sort((a: ClusterScore, b: ClusterScore) => b.percentage - a.percentage)

      setClusterScores(processed)
    } catch (err: any) {
      setError(err.message || 'Failed to load results')
    } finally {
      setLoading(false)
    }
  }

  const handleReadMore = (cluster: ClusterScore) => {
    setSelectedCluster(cluster)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedCluster(null)
  }

  if (loading) {
    return (
      <PageContainer>
        <div>Loading your results...</div>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-[var(--destructive)]'>Error: {error}</div>
      </div>
    )
  }

  const radarData = [
    {
      name: 'Experiential',
      value: clusterScores.find((c) => c.clusterId === 1)?.percentage ?? 0,
    },
    {
      name: 'Cooperative',
      value: clusterScores.find((c) => c.clusterId === 2)?.percentage ?? 0,
    },
    {
      name: 'Goal-Driven',
      value: clusterScores.find((c) => c.clusterId === 3)?.percentage ?? 0,
    },
    {
      name: 'Mastery',
      value: clusterScores.find((c) => c.clusterId === 4)?.percentage ?? 0,
    },
  ]

  return (
    <div className='w-full min-h-screen bg-white pt-5 md:pt-8 pb-8 px-4 md:px-6'>
      <div className='w-full max-w-[428px] md:max-w-[1176px] mx-auto flex flex-col'>
        {/* Powered by AIDA badge */}
        <PoweredByAida />

        {/* Header */}
        <header className='mb-8'>
          <div className='mt-3 md:mt-4 flex items-start gap-5'>
            <img src='/images/janoos-sm-icon.svg' className='w-14 h-14 rounded-full object-cover flex-shrink-0' alt='' />
            <h1 className='text-[34px] md:text-[48px] leading-tight md:leading-[60px] font-bold text-[var(--color-grey-700)] [font-family:var(--font-effra)]'>
              Hi {userName}!
            </h1>
          </div>
          <p className='mt-2 md:mt-3 text-[28px] leading-9 font-bold text-[var(--color-grey-700)] [font-family:var(--font-effra)]'>
            Welcome to your learning modalities dashboard.
          </p>
          <p className='mt-3 md:mt-4 text-sm leading-5 font-normal text-[var(--color-grey-600)] max-w-[1030px]'>
            This dashboard shows your learning modalities — how you best process and retain information. By understanding your distribution, you can use
            strategies that match your strengths. Your assistant will help you apply these insights to support your success.
          </p>
        </header>

        {/* Tabs */}
        <div className='mb-6 md:border-b md:border-[var(--color-grey-200)]'>
          <nav className='flex gap-8 md:-mb-px'>
            <button className='pb-3 text-xl font-bold leading-[30px] text-[var(--primary)] md:border-b-[3px] md:border-[var(--primary)] [font-family:var(--font-proxima-nova)]'>
              My learning modality distribution
            </button>
          </nav>
        </div>

        {/* Section title & note */}
        <section className='mb-6'>
          <p className='text-sm leading-5 font-normal text-[var(--color-curtin-primary-black-90)] [font-family:var(--font-proxima-nova)] max-w-[1030px]'>
            These results offer a starting point for understanding how you learn best. They aren&apos;t fixed and can change over time.
          </p>
        </section>

        {/* Mobile (<768px): Radar chart */}
        {clusterScores.length > 0 && (
          <div className='md:hidden mb-6'>
            <ModalityRadar data={radarData} hideTitle outerRadius='55%' cx='46%' cy='50%' />
          </div>
        )}

        {/* Mobile (<768px): cards stacked */}
        <section className='md:hidden grid grid-cols-1 gap-6'>
          {clusterScores.map((s) => (
            <article key={s.clusterId} className='p-5 bg-gray-100 border border-gray-100 rounded-lg'>
              <h4 className='text-lg font-semibold leading-7 text-[var(--color-curtin-secondary-black-100)] [font-family:var(--font-proxima-nova)]'>
                {s.name}
              </h4>
              <div className='mt-2 text-[40px] font-bold leading-7 text-[var(--primary)] [font-family:var(--font-effra)]'>{s.percentage} %</div>
              <p className='mt-3 text-sm leading-5 text-[var(--color-grey-600)] line-clamp-5'>{s.description}</p>
              <button
                onClick={() => handleReadMore(s)}
                className='mt-3 flex items-center gap-4 text-[var(--primary)] text-base font-semibold leading-7 [font-family:var(--font-proxima-nova)] hover:underline'>
                Read more
                <span className='flex items-center justify-center w-8 h-8 rounded-2xl bg-curtin-light-purple'>
                  <ArrowRight className='h-[18px] w-[18px]' />
                </span>
              </button>
            </article>
          ))}
        </section>

        {/* Tablet (768px–1023px): Radar full-width, then primary card below, secondary cards 2-col */}
        {clusterScores.length > 0 && (
          <div className='hidden md:block lg:hidden mb-6'>
            <div className='mb-2 text-center text-sm text-[var(--color-grey-600)]'>Learning Modality Map</div>
            <ModalityRadar data={radarData} hideTitle outerRadius='55%' cx='50%' cy='50%' />
          </div>
        )}

        {clusterScores.length > 0 && (
          <article className='hidden md:flex lg:hidden bg-curtin-secondary-gray-07 px-10 py-[30px] rounded-2xl flex-col justify-between mb-6'>
            <div className='flex flex-col gap-2.5'>
              <h3 className='text-lg font-semibold leading-7 text-[var(--color-curtin-secondary-black-100)] [font-family:var(--font-proxima-nova)]'>
                {clusterScores[0].name}
              </h3>
              <div className='text-[40px] leading-7 font-bold text-[var(--primary)] [font-family:var(--font-effra)]'>{clusterScores[0].percentage} %</div>
            </div>
            <p className='text-sm leading-5 text-[var(--color-grey-600)] my-[30px]'>{clusterScores[0].description}</p>
            <button
              onClick={() => handleReadMore(clusterScores[0])}
              className='flex items-center gap-4 text-[var(--primary)] text-base font-semibold leading-7 [font-family:var(--font-proxima-nova)] hover:underline self-start'>
              Read more
              <span className='flex items-center justify-center w-8 h-8 rounded-2xl bg-curtin-light-purple'>
                <ArrowRight className='h-[18px] w-[18px]' />
              </span>
            </button>
          </article>
        )}

        {clusterScores.length > 1 && (
          <section className='hidden md:grid lg:hidden grid-cols-2 gap-6 mb-6'>
            {clusterScores.slice(1).map((s) => (
              <article key={s.clusterId} className='p-5 bg-[var(--color-curtin-primary-background)] rounded-2xl flex flex-col justify-between'>
                <div className='flex flex-col gap-2.5'>
                  <h4 className='text-lg font-semibold leading-7 text-[var(--color-curtin-secondary-black-100)] [font-family:var(--font-proxima-nova)]'>
                    {s.name}
                  </h4>
                  <div className='text-[40px] font-bold leading-7 text-[var(--primary)] [font-family:var(--font-effra)]'>{s.percentage} %</div>
                </div>
                <p className='text-sm leading-5 text-[var(--color-grey-600)] line-clamp-5 my-5'>{s.description}</p>
                <button
                  onClick={() => handleReadMore(s)}
                  className='flex items-center gap-4 text-[var(--primary)] text-base font-semibold leading-7 [font-family:var(--font-proxima-nova)] hover:underline self-start'>
                  Read more
                  <span className='flex items-center justify-center w-8 h-8 rounded-2xl bg-curtin-light-purple'>
                    <ArrowRight className='h-[18px] w-[18px]' />
                  </span>
                </button>
              </article>
            ))}
          </section>
        )}

        {/* Desktop (≥1024px): Radar + primary card side-by-side, then remaining cards in 3-col grid */}
        {clusterScores.length > 0 && (
          <div className='hidden lg:grid grid-cols-2 gap-6 mb-10 items-stretch'>
            {/* Radar chart */}
            <div className='flex flex-col'>
              <div className='text-center text-sm text-[var(--color-grey-600)] mb-2'>Learning Modality Map</div>
              <ModalityRadar data={radarData} hideTitle outerRadius='75%' cx='50%' />
            </div>

            {/* Primary modality card */}
            <article className='bg-curtin-secondary-gray-07 px-10 py-[30px] rounded-2xl flex flex-col justify-between'>
              <div className='flex flex-col gap-2.5'>
                <h3 className='text-lg font-semibold leading-7 text-[var(--color-curtin-secondary-black-100)] [font-family:var(--font-proxima-nova)]'>
                  {clusterScores[0].name}
                </h3>
                <div className='text-[40px] leading-7 font-bold text-[var(--primary)] [font-family:var(--font-effra)]'>{clusterScores[0].percentage} %</div>
              </div>
              <p className='text-sm leading-5 text-[var(--color-grey-600)] my-[30px]'>{clusterScores[0].description}</p>
              <button
                onClick={() => handleReadMore(clusterScores[0])}
                className='flex items-center gap-4 text-[var(--primary)] text-base font-semibold leading-7 [font-family:var(--font-proxima-nova)] hover:underline self-start'>
                Read more
                <span className='flex items-center justify-center w-8 h-8 rounded-2xl bg-curtin-light-purple'>
                  <ArrowRight className='h-[18px] w-[18px]' />
                </span>
              </button>
            </article>
          </div>
        )}

        {clusterScores.length > 1 && (
          <section className='hidden lg:grid grid-cols-3 gap-6'>
            {clusterScores.slice(1).map((s) => (
              <article key={s.clusterId} className='p-5 bg-[var(--color-curtin-primary-background)] rounded-2xl flex flex-col justify-between'>
                <div className='flex flex-col gap-2.5'>
                  <h4 className='text-lg font-semibold leading-7 text-[var(--color-curtin-secondary-black-100)] [font-family:var(--font-proxima-nova)]'>
                    {s.name}
                  </h4>
                  <div className='text-[40px] font-bold leading-7 text-[var(--primary)] [font-family:var(--font-effra)]'>{s.percentage} %</div>
                </div>
                <p className='text-sm leading-5 text-[var(--color-grey-600)] line-clamp-5 my-5'>{s.description}</p>
                <button
                  onClick={() => handleReadMore(s)}
                  className='flex items-center gap-4 text-[var(--primary)] text-base font-semibold leading-7 [font-family:var(--font-proxima-nova)] hover:underline self-start'>
                  Read more
                  <span className='flex items-center justify-center w-8 h-8 rounded-2xl bg-curtin-light-purple'>
                    <ArrowRight className='h-[18px] w-[18px]' />
                  </span>
                </button>
              </article>
            ))}
          </section>
        )}

        {/* Diagnostics*/}
        {/* <pre className="mt-10 text-xs text-[var(--color-grey-500)]">
            {JSON.stringify({ surveyId, results: clusterScores }, null, 2)}
          </pre> */}

        {/* Read More Modal */}
        <ReadMoreModal
          title={selectedCluster?.name || ''}
          description={selectedCluster?.description || ''}
          longFormDescriptor={selectedCluster?.longFormDescriptor}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      </div>
    </div>
  )
}
